/**
 * The event-specific "Setup" group inside the onboarding panel. This folds the
 * former standalone Setup Hub page (/event/:id/setup) into the subtle panel:
 * one expandable row per plan section, each buildable by Velo / adjustable /
 * done by hand, plus a one-shot intro banner on a freshly created event. Only
 * mounted while inside an event (eventId present) and while the plan has ≥1
 * unresolved section, so it inherits CapabilitiesProvider from the event shell.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { motion } from "framer-motion";
import { IconX } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/Toast/Toast";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { useEvent } from "@/lib/query/hooks/useEvent";
import { useUpdateSetupSection } from "@/lib/query/hooks/useSetupPlan";
import { useAiAvailabilityStore } from "@/lib/ai/ai-store";
import { SetupPlan, SetupSectionKey } from "@/types/SetupPlan";
import { useBuildSection } from "@/pages/event/setup/useBuildSection";
import { useSetupVisibility } from "@/pages/event/setup/useVisibleSetupSections";
import { workflowsBlocksOnEmails } from "@/pages/event/setup/visibleSections";
import {
  useSetupAutoDetect,
  reconcileSetupSections,
  clearSetupDetectLatch,
} from "@/pages/event/setup/useSetupAutoDetect";
import { OnboardingSetupRow } from "./OnboardingSetupRow";

interface Props {
  eventId: string;
  plan: SetupPlan;
  showIntro: boolean;
  onDismissIntro: () => void;
}

export function OnboardingSetupTrack({ eventId, plan, showIntro, onDismissIntro }: Props) {
  const t = useTranslations("setupHub");
  const tOnb = useTranslations("onboarding");
  const { showToast } = useToast();
  const { can } = useCapabilities();
  const canWrite = can("event-setup:write");
  const aiAvailable = can("ai:use");

  const queryClient = useQueryClient();
  const eventQuery = useEvent(eventId || null);
  const updateSection = useUpdateSetupSection(eventId);
  const { build, buildingKey } = useBuildSection(eventId, eventQuery.data);
  // The list shows only OPEN sections (skipped/resolved drop off) whose module
  // this event actually has, ordered like the left sidebar — collapsed by
  // default, the user taps one to reveal its actions. `moduleVisibleKeys` is
  // the sweep's scope: DONE_MANUALLY is final, so auto-detect must never close
  // a section the panel doesn't show.
  const { open: openSections, moduleVisibleKeys } = useSetupVisibility(eventId, plan);
  useSetupAutoDetect(eventId, plan, canWrite, buildingKey != null, moduleVisibleKeys);

  // "Discuss with Velo" hands a section to the MAIN event assistant (it has the
  // real build tools + get_setup_plan). That build never touches the plan doc,
  // so when the drawer we opened closes, re-run detection to flip freshly-built
  // sections to DONE_MANUALLY (they then drop off the list).
  const drawerOpen = useAiAvailabilityStore((s) => s.drawerOpen);
  const prevDrawerOpen = useRef(drawerOpen);
  const veloOpenedForSetup = useRef(false);
  useEffect(() => {
    const closed = prevDrawerOpen.current && !drawerOpen;
    prevDrawerOpen.current = drawerOpen;
    if (!closed || !veloOpenedForSetup.current) return;
    veloOpenedForSetup.current = false;
    // Skip while a LOCAL build (fallback button) runs — it owns the status and
    // reconcile could race its BUILDING claim (the module latch was already
    // cleared in handleAdjust, so a remount still re-detects).
    if (buildingKey == null)
      void reconcileSetupSections(eventId, plan, canWrite, queryClient, moduleVisibleKeys);
  }, [drawerOpen, eventId, plan, canWrite, queryClient, buildingKey, moduleVisibleKeys]);

  const handleAdjust = useCallback(
    (key: SetupSectionKey) => {
      const evt = new CustomEvent("openAiAssistant", {
        cancelable: true,
        detail: {
          prompt: t("veloPrimer", { section: t(`sections.${key}.title`) }),
          autoSend: true,
        },
      });
      // dispatchEvent returns false when a listener called preventDefault, i.e.
      // the assistant actually honored the open. Only then arm the re-detect,
      // so a click that no-ops (AI still resolving) can't leak a spurious sweep.
      const honored = !window.dispatchEvent(evt);
      if (honored) {
        veloOpenedForSetup.current = true;
        clearSetupDetectLatch(eventId);
      }
    },
    [t, eventId],
  );

  const sections = plan.sections;
  const [expandedKey, setExpandedKey] = useState<SetupSectionKey | null>(null);

  const openCount = openSections.length;

  // WORKFLOWS builds bind to templates created by the EMAILS build. ONE
  // definition, shared with the filter that drops a WORKFLOWS row whose EMAILS
  // row isn't on screen — the two used to be separate copies and drifted.
  const emailsSection = sections.find((s) => s.key === "EMAILS");
  const workflowsSection = sections.find((s) => s.key === "WORKFLOWS");
  const workflowsHasProposal = !!workflowsSection?.proposal?.trim();
  const emailsStillBuildable =
    !!emailsSection?.proposal?.trim() && emailsSection.status !== "DONE_MANUALLY";
  const workflowsBlocked = workflowsBlocksOnEmails(workflowsSection, sections);
  const workflowsManualOnly =
    workflowsHasProposal && !emailsStillBuildable && emailsSection?.status !== "BUILT";

  const handleBuild = useCallback(
    async (key: SetupSectionKey) => {
      const outcome = await build(key);
      if (outcome === "built") showToast(t("toast.built"), "success");
      else if (outcome === "partial") showToast(t("toast.partial"), "info");
      else if (outcome === "failed") showToast(t("toast.failed"), "error");
      else if (outcome === "nothingToBuild") showToast(t("toast.nothingToBuild"), "info");
      else if (outcome === "stale") showToast(t("toast.stale"), "info");
    },
    [build, showToast, t],
  );

  const handleSkip = useCallback(
    (key: SetupSectionKey) => {
      updateSection.mutate({ key, update: { status: "SKIPPED" } });
    },
    [updateSection],
  );

  return (
    <div className="border-t outline-secondary px-1.5 py-1.5">
      <div className="flex items-center justify-between px-2.5 pt-2 pb-1">
        <span className="body-4-medium font-tertiary uppercase tracking-widest">
          {tOnb("eventSetup.title")}
        </span>
        {openCount > 0 && (
          <span className="body-4-medium font-tertiary tabular-nums">
            {tOnb("eventSetup.openCount", { count: openCount })}
          </span>
        )}
      </div>

      {showIntro && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-1.5 mb-1.5 flex items-start gap-2 rounded-lg surface-blue px-2.5 py-2"
        >
          <p className="flex-1 body-3-regular font-blue">{tOnb("eventSetup.intro")}</p>
          <button
            type="button"
            onClick={onDismissIntro}
            className="shrink-0 rounded p-0.5 font-blue transition-opacity hover:opacity-70 cursor-pointer"
            aria-label={tOnb("eventSetup.dismissIntro")}
          >
            <IconX size={13} />
          </button>
        </motion.div>
      )}

      <div className="max-h-72 overflow-y-auto custom-scrollbar">
        {openSections.map((section) => (
          <OnboardingSetupRow
            key={section.key}
            eventId={eventId}
            section={section}
            canWrite={canWrite}
            workflowsBlocked={section.key === "WORKFLOWS" && workflowsBlocked}
            workflowsManualOnly={section.key === "WORKFLOWS" && workflowsManualOnly}
            building={buildingKey === section.key}
            anyBuildInFlight={buildingKey != null}
            expanded={expandedKey === section.key}
            onToggle={() =>
              setExpandedKey((prev) => (prev === section.key ? null : section.key))
            }
            onBuild={(key) => void handleBuild(key)}
            onSkip={handleSkip}
            onAdjust={aiAvailable && canWrite ? handleAdjust : undefined}
          />
        ))}
      </div>
    </div>
  );
}
